import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";

// Versión simplificada de concluyendo.php: por ahora no guarda precios ni
// observacion_factura (eso queda para más adelante) — solo lista los equipos
// asignados a la OT para que el usuario los verifique, y al Concluir los pasa
// a "Activo" y avanza la OT a "Concluido".
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

// Texto precargado a pedido del usuario — recuerda qué trae cada tipo de equipo,
// queda editable/borrable antes de Concluir.
const NOTA_EQUIPOS_DEFAULT = `Nota: Cada Radio Portátil incluye: antena, batería, clip para cinturón, cargador y transformador.

Nota: Cada Radio Móvil incluye: antena, cable de alimentación y micrófono.

Nota: Cada Radio Base incluye: antena, cable de alimentación, micrófono y fuente de poder.

Nota: Cada AVL GPRS incluye: antena GPS, antena GPRS, chip de comunicación y cables de alimentación.`;

const ConcluirOT = ({ cod_ot, esTKR, onConcluido }) => {
  const [items, setItems] = useState([]);
  const [accesorios, setAccesorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [observacion, setObservacion] = useState(NOTA_EQUIPOS_DEFAULT);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  // Candado sincrónico contra doble click/doble submit — ver mismo comentario en
  // OrdenTrabajoDetalle.jsx/OrdenTrabajoPaso.jsx.
  const concluyendoRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    const peticiones = esTKR
      ? [axios.get("/api/historial-radio", { params: { cod_ot } }), axios.get("/api/historial-bateria", { params: { cod_ot } })]
      : [axios.get("/api/historial-varios", { params: { cod_ot } }), axios.get("/api/historial-chip", { params: { cod_ot } })];

    Promise.all([...peticiones, axios.get("/api/historial-accesorio", { params: { cod_ot } })])
      .then(([res1, res2, resAccesorios]) => {
        setAccesorios(resAccesorios.data || []);
        let filas = [];
        if (esTKR) {
          filas = [
            ...(res1.data || []).filter((r) => r.estado === "Asignado").map((r) => ({
              key: `radio-${r.id_historial}`,
              identificador: r.serie,
              tipo: "Radio",
              modelo: r.modelo,
              equipoUrl: `/api/equipos-radio/${r.equipo_id}`,
              historialUrl: `/api/historial-radio/${r.id_historial}`,
            })),
            ...(res2.data || []).filter((r) => r.estado === "Asignado").map((r) => ({
              key: `bateria-${r.id_historial}`,
              identificador: r.serie,
              tipo: "Batería",
              modelo: r.modelo,
              equipoUrl: `/api/equipos-bateria/${r.equipo_id}`,
              historialUrl: `/api/historial-bateria/${r.id_historial}`,
            })),
          ];
        } else {
          filas = [
            ...(res1.data || []).filter((r) => r.estado === "Asignado").map((r) => ({
              key: `varios-${r.id_historial}`,
              identificador: r.serie,
              tipo: r.tipo,
              modelo: r.modelo,
              equipoUrl: `/api/equipos-varios/${r.equipo_id}`,
              historialUrl: `/api/historial-varios/${r.id_historial}`,
            })),
            ...(res2.data || []).filter((r) => r.estado === "Asignado").map((r) => ({
              key: `chip-${r.id_historial}`,
              identificador: r.numero,
              tipo: "Chip",
              modelo: r.modelo,
              equipoUrl: `/api/equipos-chip/${r.equipo_id}`,
              historialUrl: `/api/historial-chip/${r.id_historial}`,
            })),
          ];
        }
        setItems(filas);
      })
      .catch((err) => console.error("Error al cargar equipos para concluir", err))
      .finally(() => setLoading(false));
  }, [cod_ot, esTKR]);

  const concluir = async () => {
    if (!window.confirm('¿Concluir esta OT? Los equipos asignados pasarán a "Activo".')) return;
    if (concluyendoRef.current) return;
    concluyendoRef.current = true;

    // Se abre la pestaña ya (en blanco) antes de cualquier await, en el mismo gesto de
    // click del usuario — si se abre después de un await, el navegador la bloquea como popup.
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Generando Nota de Entrega...";
      pdfWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Concluyendo OT y generando la Nota de Entrega...</div>';
    }

    setGuardando(true);
    setError("");
    try {
      for (const item of items) {
        await axios.put(item.equipoUrl, { estado: "Activo" });
        await axios.put(item.historialUrl, { estado: "Activo" });
      }

      const res = await axios.patch(`/api/ot-solicitudes/${cod_ot}/avanzar`, { observacion });
      if (onConcluido) onConcluido(res.data);

      if (pdfWindow) {
        try {
          const { data } = await axios.get(`/api/ot-solicitudes/${cod_ot}/nota-entrega.pdf`, { responseType: "blob" });
          const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
          pdfWindow.location.replace(pdfUrl);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        } catch {
          pdfWindow.close();
        }
      }
    } catch (err) {
      if (pdfWindow) pdfWindow.close();
      setError(err?.response?.data?.error || "No se pudo concluir la OT.");
    } finally {
      concluyendoRef.current = false;
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos en esta OT
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 320, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Tipo</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!items.length ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay equipos asignados a esta OT.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.key} hover>
                  <TableCell>{item.identificador}</TableCell>
                  <TableCell>{item.tipo}</TableCell>
                  <TableCell>{item.modelo}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Accesorios asignados
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 220, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={headCellSx}>Cantidad</TableCell>
              <TableCell sx={headCellSx}>Fecha</TableCell>
              <TableCell sx={headCellSx}>Observación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!accesorios.length ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No se asignaron accesorios en esta OT.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              accesorios.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.modelo}</TableCell>
                  <TableCell>{a.cantidad}</TableCell>
                  <TableCell>{String(a.fecha || "").slice(0, 10)}</TableCell>
                  <TableCell>{a.observacion}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TextField
        label="Observación de Conclusión"
        fullWidth
        multiline
        rows={8}
        value={observacion}
        onChange={(e) => setObservacion(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" onClick={concluir} disabled={guardando}>
          {guardando ? <CircularProgress size={20} /> : "Concluir OT"}
        </Button>
      </Box>
    </Box>
  );
};

export default ConcluirOT;
